import { useState, useEffect, useMemo } from 'react'
import { publicAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import SearchBar from '../components/SearchBar'
import PostnummerFilter from '../components/PostnummerFilter'
import ExportButtons from '../components/ExportButtons'
import DateFilterBar, { OutputMode, getDateRangeForMode, formatNorwegianDate } from '../components/DateFilterBar'
import { useYearDates } from '../contexts/YearContext'
import { useVehicleFilter } from '../contexts/VehicleFilterContext'
import PeriodInfoBanner from '../components/PeriodInfoBanner'
import { useMaxDataDate } from '../hooks/useMaxDataDate'
import { Car, TrendingUp, Package, Zap, ChevronRight, ArrowLeft, ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react'
import { Bar } from 'react-chartjs-2'
import { ColumnDefinition } from '../utils/exportToCsv'
import { usePersistedState } from '../hooks/usePersistedState'
import { useMinePostnummer } from '../hooks/useMinePostnummer'
import { useTranslation } from 'react-i18next'
import { localizeApiDetail } from '../i18n/apiError'

// Helper function to safely parse numeric values
const toNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined) return 0
  return typeof value === 'string' ? parseFloat(value) : value
}

interface MasterModelStats {
  ranking?: number
  master_model_id: number
  brand_key: string
  brand: string
  master_model: string
  segment_id: number | null
  body_type: string | null
  total_count: number
  market_share_percentage: number
  is_electric: 0 | 1
  ev_count: number
  ev_percentage: number
  leasing_count: number
  leasing_percentage: number
  private_leasing_count: number
  private_leasing_percentage: number
  company_leasing_count: number
  company_leasing_percentage: number
  company_sales_count: number
  company_sales_percentage: number
  avg_owner_age: number | null
}

interface ModelVariantStats {
  ranking?: number  // Added dynamically based on order
  model_variant: string
  brand: string
  total_count: number
  market_share_percentage: number
  ev_count: number
  ev_percentage: number
  leasing_count: number
  leasing_percentage: number
  private_leasing_count: number
  private_leasing_percentage: number
  company_leasing_count: number
  company_leasing_percentage: number
  company_sales_count: number
  company_sales_percentage: number
  avg_owner_age: number | null
}

export default function ModelVariantAnalytics() {
  const { t } = useTranslation('analytics')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [masterModels, setMasterModels] = useState<MasterModelStats[]>([])
  const [filteredMasterModels, setFilteredMasterModels] = useState<MasterModelStats[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [modelsSummary, setModelsSummary] = useState<any>(null)
  
  // Date range state from year context
  const { startDate, setStartDate, endDate, setEndDate, selectedYear, maxSvvDate } = useYearDates('model-variant-analytics')
  
  // Vehicle filter
  const { vehicleFilter, getVarebilParam } = useVehicleFilter()
  
  // Postal code filter state
  const [postnummerFilterEnabled, setPostnummerFilterEnabled] = usePersistedState('model-variant-analytics:postnummerFilterEnabled', false)
  const minePostnummer = useMinePostnummer(postnummerFilterEnabled)
  
  // Date range validation
  const [dateRangeAlert, setDateRangeAlert] = useState<string | null>(null)
  const [outputMode, setOutputMode] = usePersistedState<OutputMode>('bp:filter:outputMode', 'year_to_date')
  const [customStartDate, setCustomStartDate] = usePersistedState('bp:filter:customStartDate', '')
  const [customEndDate, setCustomEndDate] = usePersistedState('bp:filter:customEndDate', '')
  const maxDataDate = useMaxDataDate()

  // Sort state
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)
  const [variantsSortConfig, setVariantsSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)

  // Model Variants view state (when master model is selected)
  const [selectedMasterModel, setSelectedMasterModel] = useState<MasterModelStats | null>(null)
  const [modelVariants, setModelVariants] = useState<ModelVariantStats[]>([])
  const [variantsLoading, setVariantsLoading] = useState(false)
  const [variantsError, setVariantsError] = useState<string | null>(null)

  useEffect(() => {
    fetchMasterModels()
  }, [selectedYear, vehicleFilter])

  // Validate date range (max 366 days)
  const validateDateRange = (start: Date, end: Date): boolean => {
    const diffTime = Math.abs(end.getTime() - start.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays > 366) {
      setDateRangeAlert(t('common.dateRangeTooLarge', { days: diffDays }))
      return false
    }
    
    setDateRangeAlert(null)
    return true
  }

  const handleApplyFilters = () => {
    if (outputMode === 'custom') {
      if (!customStartDate || !customEndDate) return
      const s = new Date(customStartDate)
      const e = new Date(customEndDate)
      if (!validateDateRange(s, e)) return
      setStartDate(s)
      setEndDate(e)
      setTimeout(() => fetchMasterModels(), 0)
    } else {
      const range = getDateRangeForMode(outputMode, maxSvvDate)
      setStartDate(new Date(range.startDate))
      setEndDate(new Date(range.endDate))
      setDateRangeAlert(null)
      setTimeout(() => fetchMasterModels(), 0)
    }
  }

  const handleOutputModeChange = (mode: OutputMode) => {
    setOutputMode(mode)
    if (mode !== 'custom') {
      const range = getDateRangeForMode(mode, maxSvvDate)
      setStartDate(new Date(range.startDate))
      setEndDate(new Date(range.endDate))
      setDateRangeAlert(null)
      setTimeout(() => fetchMasterModels(), 0)
    }
  }

  useEffect(() => {
    const filtered = masterModels.filter(mm =>
      mm.master_model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mm.brand?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    setFilteredMasterModels(filtered)
  }, [masterModels, searchTerm])

  const fetchMasterModels = async () => {
    try {
      setLoading(true)
      setError(null)

      const start_date = startDate.toISOString().split('T')[0]
      const end_date = endDate.toISOString().split('T')[0]

      const varebilParam = getVarebilParam()
      const params: any = { start_date, end_date }
      
      // Add vehicle filter
      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }
      
      // Add postal code filter if enabled
      if (minePostnummer) {
        params.mine_postnummer = minePostnummer
      }

      // Fetch master models
      const response = await publicAPI.getMasterModels(params)
      const data = response.data.data || []
      
      // First item is the summary row (ranking === null), rest are model variant rows
      const [apiSummary, ...modelRows] = data
      setMasterModels(modelRows)
      
      // Use the summary row from master-models endpoint directly
      if (apiSummary && apiSummary.ranking === null) {
        setModelsSummary({
          total_count: apiSummary.total_count,
          ev_count: apiSummary.ev_count,
          ev_percentage: apiSummary.ev_percentage,
          leasing_count: apiSummary.leasing_count,
          leasing_percentage: apiSummary.leasing_percentage,
          company_sales_count: apiSummary.company_sales_count || 0,
          company_leasing_count: apiSummary.company_leasing_count || 0,
          company_sales_percentage: apiSummary.company_sales_percentage || 0,
          company_leasing_percentage: apiSummary.company_leasing_percentage || 0,
          avg_owner_age: modelRows.length > 0 
            ? modelRows.reduce((sum: number, mm: MasterModelStats) => sum + (mm.avg_owner_age || 0), 0) / modelRows.length
            : null
        })
      }
    } catch (err: any) {
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('common.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  // Fetch model variants for a selected master model
  const fetchMasterModelVariants = async (masterModel: MasterModelStats) => {
    try {
      setVariantsLoading(true)
      setVariantsError(null)

      const start_date = startDate.toISOString().split('T')[0]
      const end_date = endDate.toISOString().split('T')[0]

      const varebilParam = getVarebilParam()
      const params: any = { start_date, end_date, limit: 100 }
      
      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }
      
      // Add postal code filter if enabled
      if (minePostnummer) {
        params.mine_postnummer = minePostnummer
      }

      const response = await publicAPI.getMasterModelVariants(masterModel.master_model_id, params)
      setModelVariants(response.data.data || [])
    } catch (err: any) {
      console.error('Model variants API error:', err)
      setVariantsError(localizeApiDetail(err.response?.data?.detail) || err.message || t('common.loadVariantsFailed'))
    } finally {
      setVariantsLoading(false)
    }
  }

  // User clicks a master model to see variants
  const handleMasterModelClick = (masterModel: MasterModelStats) => {
    setSelectedMasterModel(masterModel)
    fetchMasterModelVariants(masterModel)
  }

  // Back from variants view to master models list
  const handleBackToList = () => {
    setSelectedMasterModel(null)
    setModelVariants([])
    setVariantsError(null)
  }


  // Sort handlers
  const handleSort = (key: string) => {
    if (sortConfig?.key === key) {
      if (sortConfig.direction === 'asc') {
        setSortConfig({ key, direction: 'desc' })
      } else {
        setSortConfig(null)
      }
    } else {
      setSortConfig({ key, direction: 'asc' })
    }
  }

  const handleVariantsSort = (key: string) => {
    if (variantsSortConfig?.key === key) {
      if (variantsSortConfig.direction === 'asc') {
        setVariantsSortConfig({ key, direction: 'desc' })
      } else {
        setVariantsSortConfig(null)
      }
    } else {
      setVariantsSortConfig({ key, direction: 'asc' })
    }
  }

  // Get sort icon
  const getSortIcon = (columnKey: string, config: typeof sortConfig) => {
    if (config?.key === columnKey) {
      return config.direction === 'asc' ? 
        <ArrowUp className="w-4 h-4 inline ml-1" /> : 
        <ArrowDown className="w-4 h-4 inline ml-1" />
    }
    return <ChevronsUpDown className="w-4 h-4 inline ml-1 opacity-40" />
  }

  // Sorted master models data (excluding TOTAL row)
  const sortedMasterModels = useMemo(() => {
    if (!sortConfig) return filteredMasterModels

    const sorted = [...filteredMasterModels].sort((a, b) => {
      let aVal: any
      let bVal: any

      switch (sortConfig.key) {
        case 'ranking':
          aVal = a.ranking || Infinity
          bVal = b.ranking || Infinity
          break
        case 'master_model':
          aVal = a.master_model.toLowerCase()
          bVal = b.master_model.toLowerCase()
          break
        case 'brand':
          aVal = a.brand.toLowerCase()
          bVal = b.brand.toLowerCase()
          break
        case 'total_count':
          aVal = toNumber(a.total_count)
          bVal = toNumber(b.total_count)
          break
        case 'market_share_percentage':
          aVal = toNumber(a.market_share_percentage)
          bVal = toNumber(b.market_share_percentage)
          break
        case 'leasing_percentage':
          aVal = toNumber(a.leasing_percentage)
          bVal = toNumber(b.leasing_percentage)
          break
        case 'company_percentage':
          aVal = toNumber(a.company_leasing_percentage) + toNumber(a.company_sales_percentage)
          bVal = toNumber(b.company_leasing_percentage) + toNumber(b.company_sales_percentage)
          break
        case 'avg_owner_age':
          aVal = a.avg_owner_age !== null ? toNumber(a.avg_owner_age) : -Infinity
          bVal = b.avg_owner_age !== null ? toNumber(b.avg_owner_age) : -Infinity
          break
        default:
          return 0
      }

      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
    })

    return sorted
  }, [filteredMasterModels, sortConfig])

  // Sorted variants data
  const sortedVariants = useMemo(() => {
    if (!variantsSortConfig) return modelVariants

    const sorted = [...modelVariants].sort((a, b) => {
      let aVal: any
      let bVal: any

      switch (variantsSortConfig.key) {
        case 'rank':
        case 'ranking':
          // Sort by the ranking field
          aVal = a.ranking || Infinity
          bVal = b.ranking || Infinity
          break
        case 'model_variant':
          aVal = a.model_variant.toLowerCase()
          bVal = b.model_variant.toLowerCase()
          break
        case 'total_count':
          aVal = toNumber(a.total_count)
          bVal = toNumber(b.total_count)
          break
        case 'market_share_percentage':
          aVal = toNumber(a.market_share_percentage)
          bVal = toNumber(b.market_share_percentage)
          break
        case 'leasing_percentage':
          aVal = toNumber(a.leasing_percentage)
          bVal = toNumber(b.leasing_percentage)
          break
        case 'private_leasing_percentage':
          aVal = toNumber(a.private_leasing_percentage)
          bVal = toNumber(b.private_leasing_percentage)
          break
        case 'company_leasing_percentage':
          aVal = toNumber(a.company_leasing_percentage)
          bVal = toNumber(b.company_leasing_percentage)
          break
        case 'company_sales_percentage':
          aVal = toNumber(a.company_sales_percentage)
          bVal = toNumber(b.company_sales_percentage)
          break
        default:
          return 0
      }

      if (typeof aVal === 'string') {
        return variantsSortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return variantsSortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
    })

    return sorted
  }, [modelVariants, variantsSortConfig])

  // Column definitions for exports
  const masterModelsColumns: ColumnDefinition[] = [
    { 
      key: 'ranking', 
      label: t('common.rank'),
      format: (val) => val ? val.toString() : '-'
    },
    { key: 'master_model', label: t('common.model') },
    { key: 'brand', label: t('common.brand') },
    { 
      key: 'total_count', 
      label: t('common.registrations'),
      format: (val) => toNumber(val).toLocaleString('nb-NO')
    },
    { 
      key: 'market_share_percentage', 
      label: t('common.marketSharePct'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2}) + '%'
    },
    { 
      key: 'leasing_percentage', 
      label: 'Leasing (%)',
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1}) + '%'
    },
    { 
      key: 'company_percentage', 
      label: t('modelVariant.companyCarPctExport'),
      format: (val, row?: any) => {
        if (row) {
          const total = toNumber(row.company_leasing_percentage) + toNumber(row.company_sales_percentage)
          return total.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1}) + ' %'
        }
        return toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1}) + '%'
      }
    },
    { 
      key: 'avg_owner_age', 
      label: t('common.avgOwnerAge'),
      format: (val) => val !== null ? toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1}) : '-'
    },
  ]

  const variantsColumns: ColumnDefinition[] = [
    { 
      key: 'ranking', 
      label: t('common.rank'),
      format: (val) => val ? val.toString() : '-'
    },
    { key: 'model_variant', label: 'Variant' },
    { 
      key: 'total_count', 
      label: t('common.count'),
      format: (val) => toNumber(val).toLocaleString('nb-NO')
    },
    { 
      key: 'market_share_percentage', 
      label: t('common.marketSharePct'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2}) + '%'
    },
    { 
      key: 'leasing_percentage', 
      label: 'Leasing (%)',
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1}) + '%'
    },
    { 
      key: 'private_leasing_percentage', 
      label: t('modelVariant.privateLeasingExport'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1}) + '%'
    },
    { 
      key: 'company_leasing_percentage', 
      label: t('modelVariant.companyLeasingExport'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1}) + '%'
    },
    { 
      key: 'company_sales_percentage', 
      label: t('common.companySalesPctExport'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1}) + '%'
    },
  ]

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMessage message={error} />

  // Model Variants view (when master model is selected)
  if (selectedMasterModel) {
    return (
      <div className="space-y-6">
        {/* Header with back button */}
        <div className="flex items-center gap-4">
          <button
            onClick={handleBackToList}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-dark-card hover:bg-gray-200 dark:hover:bg-dark-border rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t('common.backToMasterModels')}</span>
          </button>
        </div>

        {/* Selected Model Info */}
        <div className="card p-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {selectedMasterModel.brand} {selectedMasterModel.master_model}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('common.modelVariantsFor', { model: selectedMasterModel.master_model })}
          </p>
          
          {/* Summary stats for this model */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="bg-gray-50 dark:bg-dark-border p-3 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('modelVariant.total')}</p>
              <p className="text-lg font-bold">{selectedMasterModel.total_count.toLocaleString('nb-NO')}</p>
            </div>
            <div className="bg-gray-50 dark:bg-dark-border p-3 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('common.evShare')}</p>
              <p className="text-lg font-bold text-green-600">{selectedMasterModel.ev_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %</p>
            </div>
            <div className="bg-gray-50 dark:bg-dark-border p-3 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">Leasing</p>
              <p className="text-lg font-bold text-blue-600">{selectedMasterModel.leasing_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %</p>
            </div>
            <div className="bg-gray-50 dark:bg-dark-border p-3 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('modelVariant.companyCar')}</p>
              <p className="text-lg font-bold text-orange-600">
                {(selectedMasterModel.company_leasing_percentage + selectedMasterModel.company_sales_percentage).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
              </p>
            </div>
          </div>
        </div>

        {/* Variants Loading/Error/Content */}
        {variantsLoading ? (
          <LoadingSpinner />
        ) : variantsError ? (
          <ErrorMessage message={variantsError} />
        ) : modelVariants.length === 0 ? (
          <div className="card p-12 text-center">
            <Car className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              {t('modelVariant.noVariantsForModel')}
            </p>
          </div>
        ) : (
          <div className="card p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {t('modelVariant.variantsCount', { n: modelVariants.length })}
              </h2>
              <div className="flex items-center gap-2">
                {variantsSortConfig && (
                  <button 
                    onClick={() => setVariantsSortConfig(null)}
                    className="btn-secondary text-sm"
                  >
                    {t('common.resetSort')}
                  </button>
                )}
                <ExportButtons
                  data={sortedVariants}
                  columns={variantsColumns}
                  metadata={{
                    title: t('exportTitles.modelVariants', { brand: selectedMasterModel.brand, model: selectedMasterModel.master_model }),
                    dateRange: t('common.dateRange', { start: formatNorwegianDate(startDate), end: formatNorwegianDate(endDate) }),
                    filters: [
                      vehicleFilter === 'personbil' ? t('common.filterPersonbil') : 
                      vehicleFilter === 'varebil' ? t('common.filterVarebil') : '',
                      minePostnummer ? t('common.postnummerFilter', { value: minePostnummer }) : ''
                    ].filter(f => f),
                  }}
                  disabled={variantsLoading}
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table data-table-compact">
                <thead>
                  <tr>
                    <th className="text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleVariantsSort('ranking')} title={t('common.originalRanking')}>
                      # {getSortIcon('ranking', variantsSortConfig)}
                    </th>
                    <th className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleVariantsSort('model_variant')}>
                      Variant {getSortIcon('model_variant', variantsSortConfig)}
                    </th>
                    <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleVariantsSort('total_count')}>
                      {t('common.count')} {getSortIcon('total_count', variantsSortConfig)}
                    </th>
                    <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleVariantsSort('market_share_percentage')}>
                      {t('common.marketShare')} {getSortIcon('market_share_percentage', variantsSortConfig)}
                    </th>
                    <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleVariantsSort('leasing_percentage')}>
                      Leasing % {getSortIcon('leasing_percentage', variantsSortConfig)}
                    </th>
                    <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleVariantsSort('private_leasing_percentage')}>
                      {t('modelVariant.privateLeasing')} {getSortIcon('private_leasing_percentage', variantsSortConfig)}
                    </th>
                    <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleVariantsSort('company_leasing_percentage')}>
                      {t('modelVariant.companyLeasing')} {getSortIcon('company_leasing_percentage', variantsSortConfig)}
                    </th>
                    <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleVariantsSort('company_sales_percentage')}>
                      {t('common.companySalesPct')} {getSortIcon('company_sales_percentage', variantsSortConfig)}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedVariants.map((variant, index) => {
                    return (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-dark-border transition-colors">
                        <td className="text-center text-gray-700 dark:text-gray-300">
                          {variant.ranking ?? '—'}
                        </td>
                        <td className="font-semibold">{variant.model_variant}</td>
                      <td className="text-right font-medium">{variant.total_count.toLocaleString('nb-NO')}</td>
                      <td className="text-right">
                        <span className="text-blue-600 dark:text-blue-400">
                          {variant.market_share_percentage.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                        </span>
                      </td>
                      <td className="text-right">
                        <span className="text-blue-600 dark:text-blue-400">
                          {variant.leasing_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                        </span>
                      </td>
                      <td className="text-right">{variant.private_leasing_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %</td>
                      <td className="text-right">{variant.company_leasing_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %</td>
                      <td className="text-right">
                        <span className="text-orange-600 dark:text-orange-400">
                          {variant.company_sales_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                        </span>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Main Master Models list view
  // Prepare chart data (exclude from chart, only show actual models)
  const chartData = {
    labels: sortedMasterModels.slice(0, 20).map(mm => `${mm.brand} ${mm.master_model}`),
    datasets: [
      {
        label: t('common.registrations'),
        data: sortedMasterModels.slice(0, 20).map(mm => mm.total_count),
        backgroundColor: 'rgba(14, 165, 233, 0.7)',
        borderColor: 'rgba(14, 165, 233, 1)',
        borderWidth: 1,
      },
    ],
  }

  const chartOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        ticks: {
          callback: (value: number | string) => Number(value).toLocaleString('nb-NO', { maximumFractionDigits: 0 }),
        },
      },
    },
  }

  // Prepare master models export data with computed company percentage
  const masterModelsExportData = sortedMasterModels.map(mm => ({
    ...mm,
    company_percentage: mm.company_leasing_percentage + mm.company_sales_percentage
  }))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {t('modelVariant.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('modelVariant.subtitle')}
          </p>
        </div>
      </div>


      {/* Date Range Filter */}
      <DateFilterBar
        outputMode={outputMode}
        onOutputModeChange={handleOutputModeChange}
        customStartDate={customStartDate}
        onCustomStartDateChange={setCustomStartDate}
        customEndDate={customEndDate}
        onCustomEndDateChange={setCustomEndDate}
        onApply={handleApplyFilters}
        bottomContent={
          <PostnummerFilter
            enabled={postnummerFilterEnabled}
            onToggle={setPostnummerFilterEnabled}
            compact
          />
        }
      />

      {/* Date Range Alert */}
      {dateRangeAlert && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">
            <strong>{t('common.errorPrefix')}</strong> {dateRangeAlert}
          </p>
        </div>
      )}

      {/* Period Info Banner */}
      <PeriodInfoBanner
        maxDataDate={maxDataDate}
        outputMode={outputMode}
        periodLabel={t(`outputModes.${outputMode}`)}
        startDate={startDate.toISOString().split('T')[0]}
        endDate={endDate.toISOString().split('T')[0]}
      />

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
              <Car className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">{t('common.modelCount')}</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {masterModels.length}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">{t('common.totalRegistrations')}</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {modelsSummary ? modelsSummary.total_count.toLocaleString('nb-NO') : '0'}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <Zap className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">{t('modelVariant.avgEvShare')}</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {masterModels.length > 0
                  ? (
                      masterModels.reduce((sum, mm) => sum + mm.ev_percentage, 0) /
                      masterModels.length
                    ).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})
                  : 0}
                %
              </p>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <Package className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">{t('modelVariant.topModel')}</p>
              <p className="text-sm font-bold text-gray-900 dark:text-gray-100 truncate">
                {masterModels[0]?.master_model || 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="card p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          {t('modelVariant.top20')}
        </h2>
        {masterModels.length > 0 && (
          <div className="h-[400px]">
            <Bar data={chartData} options={chartOptions} />
          </div>
        )}
      </div>

      {/* Search and Table */}
      <div className="card p-6">
        <div className="mb-4">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t('common.searchModelOrBrand')}
          />
        </div>

        <div className="flex justify-between items-center mb-4">
          <div>
            {sortConfig && (
              <button 
                onClick={() => setSortConfig(null)}
                className="btn-secondary text-sm"
              >
                {t('common.resetSort')}
              </button>
            )}
          </div>
          <ExportButtons
            data={masterModelsExportData}
            columns={masterModelsColumns}
            metadata={{
              title: t('modelVariant.title'),
              dateRange: t('common.dateRange', { start: formatNorwegianDate(startDate), end: formatNorwegianDate(endDate) }),
              filters: [
                vehicleFilter === 'personbil' ? t('common.filterPersonbil') : 
                vehicleFilter === 'varebil' ? t('common.filterVarebil') : '',
                minePostnummer ? t('common.postnummerFilter', { value: minePostnummer }) : ''
              ].filter(f => f),
            }}
            disabled={loading}
          />
        </div>

        <div className="overflow-x-auto">
          <table className="data-table data-table-compact">
            <thead>
              <tr>
                <th className="text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('ranking')} title={t('modelVariant.rankingTitle')}>
                  # {getSortIcon('ranking', sortConfig)}
                </th>
                <th className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('master_model')}>
                  {t('common.model')} {getSortIcon('master_model', sortConfig)}
                </th>
                <th className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('brand')}>
                  {t('common.brand')} {getSortIcon('brand', sortConfig)}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('total_count')}>
                  {t('common.registrations')} {getSortIcon('total_count', sortConfig)}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('market_share_percentage')}>
                  {t('common.marketShare')} {getSortIcon('market_share_percentage', sortConfig)}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('leasing_percentage')}>
                  Leasing % {getSortIcon('leasing_percentage', sortConfig)}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('company_percentage')}>
                  {t('modelVariant.companyCarPct')} {getSortIcon('company_percentage', sortConfig)}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('avg_owner_age')}>
                  {t('common.avgOwnerAge')} {getSortIcon('avg_owner_age', sortConfig)}
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {/* Summary Row (TOTAL) */}
              {modelsSummary && (
                <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t-4 border-b-4 border-primary-400 dark:border-primary-600">
                  <td className="text-center py-4">
                    <span className="text-primary-600 dark:text-primary-400 font-bold">∑</span>
                  </td>
                  <td className="font-bold text-base text-primary-700 dark:text-primary-300 py-4">TOTAL</td>
                  <td className="py-4">
                    <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">—</span>
                  </td>
                  <td className="text-right font-bold text-base py-4">{modelsSummary.total_count.toLocaleString('nb-NO')}</td>
                  <td className="text-right py-4">
                    <span className="font-bold text-base text-blue-600 dark:text-blue-400">
                      100.0%
                    </span>
                  </td>
                  <td className="text-right py-4">
                    <span className="font-bold text-base text-purple-600 dark:text-purple-400">
                      {modelsSummary.leasing_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                    </span>
                  </td>
                  <td className="text-right py-4">
                    <span className="font-bold text-base text-orange-600 dark:text-orange-400">
                      {(modelsSummary.company_sales_percentage + modelsSummary.company_leasing_percentage).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                    </span>
                  </td>
                  <td className="text-right py-4">
                    <span className="font-bold text-base">
                      {modelsSummary.avg_owner_age ? modelsSummary.avg_owner_age.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1}) : '-'}
                    </span>
                  </td>
                  <td className="text-center py-4">
                    <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">—</span>
                  </td>
                </tr>
              )}
              
              {/* Model Rows */}
              {sortedMasterModels.map((mm, index) => (
                <tr 
                  key={index} 
                  className="hover:bg-gray-50 dark:hover:bg-dark-border transition-colors cursor-pointer"
                  onClick={() => handleMasterModelClick(mm)}
                >
                  <td className="text-center text-gray-700 dark:text-gray-300">
                    {mm.ranking ?? '—'}
                  </td>
                  <td className="font-semibold">{mm.master_model}</td>
                  <td>
                    <span className="text-xs px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-sm">
                      {mm.brand}
                    </span>
                  </td>
                  <td className="text-right font-medium">{mm.total_count.toLocaleString('nb-NO')}</td>
                  <td className="text-right">
                    <span className="text-blue-600 dark:text-blue-400">
                      {mm.market_share_percentage.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                    </span>
                  </td>
                  <td className="text-right">
                    <span className="text-blue-600 dark:text-blue-400">
                      {mm.leasing_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                    </span>
                  </td>
                  <td className="text-right">
                    <span className="text-orange-600 dark:text-orange-400">
                      {(mm.company_leasing_percentage + mm.company_sales_percentage).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                    </span>
                  </td>
                  <td className="text-right">
                    {mm.avg_owner_age ? mm.avg_owner_age.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1}) : '-'}
                  </td>
                  <td className="text-right">
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredMasterModels.length === 0 && (
          <div className="p-12 text-center">
            <Car className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              {t('common.noModelsFound')}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
